/*
 * File: DevelopmentFormField.tsx
 * Project: a-a
 * File Created: Sunday, 22nd June 2025 10:02:39 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Friday, 17th October 2025 8:24:20 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2025 Mutual Mobile, Mutual Mobile
 */
import React, { useEffect, useState } from 'react';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import CustomTextField from '../../../CustomTextField/CustomTextField';
import { useInput } from '../../../../../customHook/useInput';
import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  Select,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CustomAutocomplete from '../../../CustomAutocomplete/CustomAutocomplete';
import zeplinColor from '../../../../../theme/Color';
import dayjs, { Dayjs } from 'dayjs';
import CustomDatePicker from '../../../CustomDatePicker/CustomDatePicker';
import CustomCheckbox from '../../../CustomCheckbox/CustomCheckbox';
import { ClientService } from '../../../../../service/client.service';
const MenuProps = {
  PaperProps: {
    sx: {
      borderRadius: '14px',
      // padding: '16px',
    },
  },
};
const DevelopmentFormField = ({
  handleInputChange,
  value,
  dealType,
  setIsValid,
}: {
  handleInputChange: (field: string, value: any) => void;
  dealType: string;
  value: any;
  setIsValid: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xl'));
  const {
    handleChange: handleOpportunityChange,
    handleBlur: handleOpportunityBlur,
    clearInput: clearOpportunity,
    value: Opportunity,
    invalid: invalidOpportunity,
    didChange: didOpportunityChange,
  } = useInput(value?.name ? value?.name : '', () => true);
  const {
    handleChange: handleAmountChange,
    handleBlur: handleAmountBlur,
    clearInput: clearAmount,
    value: amount,
    invalid: invalidAmount,
    didChange: didAmountChange,
  } = useInput(value?.amount ? value?.amount : '', () => true);
  const {
    handleChange: handleProbabilityChange,
    handleBlur: handleProbabilityBlur,
    clearInput: clearProbability,
    value: probability,
    invalid: invalidProbability,
    didChange: didProbabilityChange,
  } = useInput(value?.probability ? value?.probability : '', () => true);
  const {
    handleChange: handleServicesChange,
    handleBlur: handleServicesBlur,
    clearInput: clearServices,
    value: services,
    invalid: invalidServices,
    didChange: didServicesChange,
  } = useInput(value?.services ? value?.services : '', () => true);
  const {
    handleChange: handleExecutiveProducerChange,
    handleBlur: handleExecutiveProducerBlur,
    clearInput: clearExecutiveProducer,
    value: executiveProducer,
    invalid: invalidExecutiveProducer,
    didChange: didExecutiveProducerChange,
  } = useInput(value?.executiveProducerFee ? value?.executiveProducerFee : '', () => true);
  const {
    handleChange: handleLoglineChange,
    handleBlur: handleLoglineBlur,
    clearInput: clearLogline,
    value: logline,
    invalid: invalidLogline,
    didChange: didLoglineChange,
  } = useInput(value?.logLine ? value?.logLine : '', () => true);

  const {
    handleChange: handleExclusivityChange,
    handleBlur: handleExclusivityBlur,
    clearInput: clearExclusivity,
    value: exclusivity,
    invalid: invalidExclusivity,
    didChange: didExclusivityChange,
  } = useInput(value?.exclusivityLanguage ? value?.exclusivity : '', () => true);
  const {
    handleChange: handleFeesChange,
    handleBlur: handleFeesBlur,
    clearInput: clearFees,
    value: fees,
    invalid: invalidFees,
    didChange: didFeesChange,
  } = useInput(value?.Guarantee__c ? value?.Guarantee__c : '', () => true);
  const {
    handleChange: handleMAGRChange,
    handleBlur: handleMAGRBlur,
    clearInput: clearMAGR,
    value: mAGR,
    invalid: invalidMAGR,
    didChange: didMAGRChange,
  } = useInput(value?.magr ? value?.magr : '', () => true);
  const {
    handleChange: handleExecutiveProducerFeesChange,
    handleBlur: handleExecutiveProducerFeesBlur,
    clearInput: clearExecutiveProducerFees,
    value: executiveProducerFees,
    invalid: invalidExecutiveProducerFees,
    didChange: didExecutiveProducerFeesChange,
  } = useInput(value?.executiveProducerFee ? value?.executiveProducerFee : '', () => true);
  const {
    handleChange: handleEstimatedChange,
    handleBlur: handleEstimatedBlur,
    clearInput: clearEstimated,
    value: estimated,
    invalid: invalidEstimated,
    didChange: didEstimatedChange,
  } = useInput(
    value?.gdEstimatedValuation
      ? value?.gdEstimatedValuation
      : value?.estimatedValuation
      ? value?.estimatedValuation
      : '',
    () => true,
  );
  const {
    handleChange: handlePartnerPercentChange,
    handleBlur: handlePartnerPercentBlur,
    clearInput: clearPartnerPercent,
    value: partnerPercent,
    invalid: invalidPartnerPercent,
    didChange: didPartnerPercentChange,
  } = useInput(value?.partnerPercent ? value?.partnerPercent : '', () => true);
  const {
    handleChange: handleOptionsChange,
    handleBlur: handleOptionsBlur,
    clearInput: clearOptions,
    value: options,
    invalid: invalidOptions,
    didChange: didOptionsChange,
  } = useInput(value?.options ? value?.options : '', () => true);
  const {
    handleChange: handleConditionsChange,
    handleBlur: handleConditionsBlur,
    clearInput: clearConditions,
    value: conditions,
    invalid: invalidConditions,
    didChange: didConditionsChange,
  } = useInput(value?.conditions ? value?.conditions : '', () => true);
  const {
    handleChange: handleFeesConditionsChange,
    handleBlur: handleFeesConditionsBlur,
    clearInput: clearFeesConditions,
    value: feesConditions,
    invalid: invalidFeesConditions,
    didChange: didFeesConditionsChange,
  } = useInput(value?.feesConditions ? value?.feesConditions : '', () => true);
  const {
    handleChange: handleRecoupabilityChange,
    handleBlur: handleRecoupabilityBlur,
    clearInput: clearRecoupability,
    value: recoupability,
    invalid: invalidRecoupability,
    didChange: didRecoupabilityChange,
  } = useInput(value?.recoupability ? value?.recoupability : '', () => true);
  const [closeDate, setCloseDate] = useState<Dayjs | null>(
    value?.closeDate ? dayjs(value?.closeDate) : null,
  );
  const [termStart, setTermStart] = useState<Dayjs | null>(
    value?.termStart ? dayjs(value?.termStart) : null,
  );
  const [termEnd, settermEnd] = useState<Dayjs | null>(
    value?.termEnd ? dayjs(value?.termEnd) : null,
  );
  const [shootStart, setShootStart] = useState<Dayjs | null>(
    value?.approxShootDateStart ? dayjs(value?.approxShootDateStart) : null,
  );
  const [shootEnd, setShootEnd] = useState<Dayjs | null>(
    value?.approxShootDateEnd ? dayjs(value?.approxShootDateEnd) : null,
  );
  const [selectedItems, setSelectedItems] = useState<{
    account: any[];
    stageName: string[];
    exclusivity: string[];
    type: string[];
    category: string[];
    gdCommission: string[];
    lostReason: string[];
    leadSource: string[];
    dealSource: string[];
    genre: string[];
    arena: string[];
    role: string[];
    productionStatus: string[];
    rating: string[];
    episodicOrder: string[];
    commitments: string[];
    releaseType: string[];
  }>({
    account: value?.accountId ? [value?.accountId] : [],
    stageName: value?.stageName ? value?.stageName : [],
    exclusivity: value?.exclusivity ? [value?.exclusivity] : [],
    type: value?.type ? [value?.type] : [],
    category:
      typeof value?.category === 'string'
        ? [...value?.category?.split(';')]
        : value?.category
        ? [...value?.category]
        : [],
    gdCommission: value?.gdCommission
      ? [value?.gdCommission]
      : value?.commission
      ? [value?.commission]
      : [],
    lostReason: value?.lostReason ? [value?.lostReason] : [],
    leadSource: value?.leadSource ? [value?.leadSource] : [],
    dealSource: value?.dealSource ? [value?.dealSource] : [],
    genre: value?.genre ? [value?.genre] : [],
    arena: value?.arena ? [value?.arena] : [],
    role: value?.role ? [value?.role] : [],
    productionStatus: value?.productionStatus ? [value?.productionStatus] : [],
    rating: value?.rating ? [value?.rating] : [],
    episodicOrder: value?.episodicOrder ? [value?.episodicOrder] : [],
    commitments: value?.commitments ? [value?.commitments] : [],
    releaseType: value?.releaseType ? [value?.releaseType] : [],
  });
  const [drowdownList, setDropdownList] = useState<any>({});

  const getDropdown = async () => {
    try {
      const res = await ClientService.getDealDropdown(dealType);
      console.log(res);
      setDropdownList(res);
    } catch (err) {
      console.log(err);
    }
  };
  useEffect(() => {
    getDropdown();
  }, []);

  useEffect(() => {
    const valid =
      !Opportunity ||
      !selectedItems?.stageName?.length ||
      !closeDate ||
      (selectedItems?.stageName[0] === 'Closed Lost' && !selectedItems?.lostReason);
    setIsValid(valid);
  }, [Opportunity, selectedItems, closeDate]);
  const handleSelection = (item: any, type: string) => {
    const key = type as keyof typeof selectedItems;
    if (Array.isArray(item)) {
      setSelectedItems((prev) => ({ ...prev, [key]: [...item] }));
    } else if (item?.target?.value) {
      const {
        target: { value },
      } = item;

      setSelectedItems((prev) => ({
        ...prev,
        [key]: typeof value === 'string' ? value.split(',') : value,
      }));
    } else {
      setSelectedItems((prev) => ({
        ...prev,
        [key]: [item],
      }));
    }
  };
  return (
    <>
      <Typography variant='heading4'>Opportunity Information</Typography>
      <CustomTextField
        style={{ width: '100%' }}
        form={true}
        onBlur={handleOpportunityBlur}
        onChange={(e) => {
          handleOpportunityChange(e);
          handleInputChange('name', e.target.value.trim());
        }}
        name='Opportunity name'
        text={Opportunity}
        error={invalidOpportunity && didOpportunityChange ? 'Please enter valid Opportunity' : ''}
        label={'Opportunity name'}
        type='text'
        tabindex={1}
        required
      />
      {/* <FormControl sx={{ width: '100%' }}>
        <CustomAutocomplete
          required={true}
          options={[{ Name: 'Marketing', Id: '1' }]}
          getOptionLabel={(option: any) => (option?.Name ? option?.Name : '')}
          label={'Account name'}
          handleDeleteItems={() => {}}
          selectedItems={selectedItems}
          handleSelection={(value) => {
            handleSelection(value, 'account');
            handleInputChange('account', value);
          }}
          value={selectedItems?.account[0]}
          handleFilter={() => {}}
          placeholder='Search people'
          currentLength={[{ Name: 'Marketing', Id: '1' }]?.length}
          totalLength={[{ Name: 'Marketing', Id: '1' }]?.length}
          next={() => {}}
          multiple={false}
        />
      </FormControl> */}
      <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            id='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? 14 : 16,
            }}
          >
            Stage<span style={{ color: zeplinColor.Error }}>*</span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.stageName}
            onChange={(e) => {
              handleSelection(e, 'stageName');
              handleInputChange('stageName', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Stage'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length ? (
                    drowdownList?.StageName?.length &&
                    drowdownList?.StageName.filter((val: any) => val === selected[0])[0] ? (
                      drowdownList?.StageName.filter((val: any) => val === selected[0])[0]
                    ) : (
                      <Skeleton
                        variant='text'
                        sx={{
                          fontSize: theme.typography.body2,
                          width: '100%',
                          // Optionally adjust other styles here
                        }}
                      />
                    )
                  ) : (
                    ''
                  );
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {drowdownList?.StageName?.length &&
              drowdownList?.StageName.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={
                      selectedItems?.stageName?.length
                        ? selectedItems?.stageName?.indexOf(name) > -1
                        : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <CustomDatePicker
          value={closeDate}
          size={isXs ? 'small' : 'medium'}
          setValue={(date) => {
            setCloseDate(date); // Update local state
            handleInputChange('closeDate', dayjs(date as Dayjs).format('YYYY-MM-DD')); // Pass the selected date to the parent component
          }}
          label={'Close date'}
          required={true}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        <CustomTextField
          disable={true}
          form={true}
          style={{ width: '100%' }}
          onBlur={() => {
            handleAmountBlur && handleAmountBlur();
          }}
          onChange={(e) => {
            handleAmountChange && handleAmountChange(e);
            handleInputChange('amount', +e.target.value);
          }}
          name={'amount'}
          text={amount ? amount : ''}
          error={invalidAmount && didAmountChange ? 'Please enter valid Amount' : ''}
          label={'Amount'}
          type='number'
          tabindex={1}
          // required
        />
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            id='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? 14 : 16,
            }}
          >
            Type<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.type}
            onChange={(e) => {
              handleSelection(e, 'type');
              handleInputChange('type', e.target.value);
              setSelectedItems((prev) => ({
                ...prev,
                commitments: [],
                episodicOrder: [],
                releaseType: [],
                rating: [],
              }));
              // Clear commitments and episodicOrder when type changes
              handleInputChange('commitments', '');
              handleInputChange('episodicOrder', '');
              handleInputChange('releaseType', '');
              handleInputChange('rating', '');
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Type'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length ? (
                    drowdownList?.Type?.length &&
                    drowdownList?.Type.filter((val: any) => val === selected[0])[0] ? (
                      drowdownList?.Type.filter((val: any) => val === selected[0])[0]
                    ) : (
                      <Skeleton
                        variant='text'
                        sx={{
                          fontSize: theme.typography.body2,
                          width: '100%',
                          // Optionally adjust other styles here
                        }}
                      />
                    )
                  ) : (
                    ''
                  );
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {drowdownList?.Type?.length &&
              drowdownList?.Type?.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={
                      selectedItems?.type?.length ? selectedItems?.type?.indexOf(name) > -1 : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        {/* <CustomTextField
          style={{ width: '100%' }}
          onBlur={() => {
            handleProbabilityBlur && handleProbabilityBlur();
          }}
          onChange={(e) => {
            handleProbabilityChange && handleProbabilityChange(e);
          }}
          name={'Probability %'}
          text={probability ? probability : ''}
          error={invalidProbability && didProbabilityChange ? 'Please enter valid Probability' : ''}
          label={'Probability %'}
          type='number'
          tabindex={2}
          // required
        /> */}
      </Box>
      <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            id='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? 14 : 16,
            }}
          >
            Genre<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.genre}
            onChange={(e) => {
              handleSelection(e, 'genre');
              handleInputChange('genre', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Genre'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length ? (
                    drowdownList?.Genre__c?.length &&
                    drowdownList?.Genre__c.filter((val: any) => val === selected[0])[0] ? (
                      drowdownList?.Genre__c.filter((val: any) => val === selected[0])[0]
                    ) : (
                      <Skeleton
                        variant='text'
                        sx={{
                          fontSize: theme.typography.body2,
                          width: '100%',
                          // Optionally adjust other styles here
                        }}
                      />
                    )
                  ) : (
                    ''
                  );
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {drowdownList?.Genre__c?.length &&
              drowdownList?.Genre__c?.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={
                      selectedItems?.genre?.length
                        ? selectedItems?.genre?.indexOf(name) > -1
                        : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <CustomTextField
          style={{ width: '100%' }}
          form={true}
          onBlur={() => {
            handleProbabilityBlur && handleProbabilityBlur();
          }}
          onChange={(e) => {
            handleProbabilityChange && handleProbabilityChange(e);
            handleInputChange('probability', +e.target.value);
          }}
          name={'Probability %'}
          text={probability ? probability : ''}
          error={invalidProbability && didProbabilityChange ? 'Please enter valid Probability' : ''}
          label={'Probability %'}
          type='number'
          tabindex={2}
          max={100}
          // required
        />
      </Box>
      <FormControl sx={{ width: '100%' }}>
        <InputLabel
          id='demo-multiple-name-label'
          size={isXs ? 'small' : 'medium'}
          sx={{
            '&.Mui-focused': { color: zeplinColor.Neutral9 },
            fontSize: isXs ? 14 : 16,
          }}
        >
          Arena<span style={{ color: zeplinColor.Error }}></span>
        </InputLabel>
        <Select
          labelId='demo-multiple-name-label'
          id='demo-multiple-name'
          multiple
          required={true}
          value={selectedItems.arena}
          onChange={(e) => {
            handleSelection(e, 'arena');
            handleInputChange('arena', e.target.value);
          }}
          input={
            <OutlinedInput
              sx={{
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '1px',
                  color: zeplinColor.Neutral9,
                  borderColor: zeplinColor.Neutral9,
                },
              }}
              label='Arena'
            />
          }
          renderValue={
            (selected) =>
              // (selected as string[]).map((val) => (
              {
                return selected?.length ? (
                  drowdownList?.Arena__c?.length &&
                  drowdownList?.Arena__c.filter((val: any) => selected.includes(val))?.length ? (
                    drowdownList?.Arena__c.filter((val: any) => selected.includes(val)).join(', ')
                  ) : (
                    <Skeleton
                      variant='text'
                      sx={{
                        fontSize: theme.typography.body2,
                        width: '100%',
                        // Optionally adjust other styles here
                      }}
                    />
                  )
                ) : (
                  ''
                );
              }
            // ))
          }
          MenuProps={MenuProps}
          IconComponent={KeyboardArrowDown}
        >
          {drowdownList?.Arena__c?.length &&
            drowdownList?.Arena__c?.map((name: any) => (
              <MenuItem key={name} value={name}>
                <Checkbox
                  checked={
                    selectedItems?.arena?.length ? selectedItems?.arena?.indexOf(name) > -1 : false
                  }
                  sx={{
                    color: zeplinColor.BasicBlack,
                    '&.Mui-checked': {
                      color: zeplinColor.BasicBlack,
                    },
                  }}
                />
                {name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      <Typography variant='heading4'>Deal Points</Typography>
      <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name-label'
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? 14 : 16,
            }}
          >
            Role<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            multiple
            required={true}
            value={selectedItems.role}
            onChange={(e) => {
              handleSelection(e, 'role');
              handleInputChange('role', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Role'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length ? (
                    drowdownList?.Role__c?.length &&
                    drowdownList?.Role__c?.filter((val: any) => selected?.includes(val))?.length ? (
                      drowdownList?.Role__c?.filter((val: any) => selected?.includes(val))?.join(
                        ', ',
                      )
                    ) : (
                      <Skeleton
                        variant='text'
                        sx={{
                          fontSize: theme.typography.body2,
                          width: '100%',
                          // Optionally adjust other styles here
                        }}
                      />
                    )
                  ) : (
                    ''
                  );
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {drowdownList?.Role__c?.length &&
              drowdownList?.Role__c?.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Checkbox
                    checked={
                      selectedItems?.role?.length ? selectedItems?.role?.indexOf(name) > -1 : false
                    }
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            id='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? 14 : 16,
            }}
          >
            Exclusivity<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.exclusivity}
            onChange={(e) => {
              handleSelection(e, 'exclusivity');
              handleInputChange('exclusivity', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Exclusivity'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length ? (
                    drowdownList?.Exclusivity__c?.length &&
                    drowdownList?.Exclusivity__c?.filter((val: any) => val === selected[0])[0] ? (
                      drowdownList?.Exclusivity__c?.filter((val: any) => val === selected[0])[0]
                    ) : (
                      <Skeleton
                        variant='text'
                        sx={{
                          fontSize: theme.typography.body2,
                          width: '100%',
                          // Optionally adjust other styles here
                        }}
                      />
                    )
                  ) : (
                    ''
                  );
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {drowdownList?.Exclusivity__c?.length &&
              drowdownList?.Exclusivity__c?.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={
                      selectedItems?.exclusivity?.length
                        ? selectedItems?.exclusivity?.indexOf(name) > -1
                        : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        {/* <FormControl sx={{ width: '100%' }}>
          <InputLabel
            id='demo-multiple-name-label'
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
            }}
          >
            Type<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.type}
            onChange={(e) => handleSelection(e, 'type')}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Type'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length
                    ? typeList?.length && typeList.filter((val) => val.id === selected[0])[0]?.name
                    : '';
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {typeList?.length &&
              typeList.map((name) => (
                <MenuItem key={name.id} value={name.id}>
                  <Radio
                    checked={
                      selectedItems?.stage?.length
                        ? selectedItems?.stage?.indexOf(name.id) > -1
                        : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl> */}
      </Box>
      <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        <CustomDatePicker
          value={termStart}
          size={isXs ? 'small' : 'medium'}
          setValue={(date) => {
            setTermStart(date); // Update local state
            handleInputChange('termStart', dayjs(date as Dayjs).format('YYYY-MM-DD')); // Pass the selected date to the parent component
          }}
          label={'Term start'}
        />

        <CustomDatePicker
          size={isXs ? 'small' : 'medium'}
          value={termEnd}
          setValue={(date) => {
            settermEnd(date); // Update local state
            handleInputChange('termEnd', dayjs(date as Dayjs).format('YYYY-MM-DD')); // Pass the selected date to the parent component
          }}
          // setValue={settermEnd}
          label={'Term end'}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        {/* <FormControl sx={{ width: '100%' }}>
          <InputLabel
            id='demo-multiple-name-label'
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
            }}
          >
            Type<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.type}
            onChange={(e) => handleSelection(e, 'type')}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Type'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length
                    ? typeList?.length && typeList.filter((val) => val.id === selected[0])[0]?.name
                    : '';
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {typeList?.length &&
              typeList.map((name) => (
                <MenuItem key={name.id} value={name.id}>
                  <Radio
                    checked={
                      selectedItems?.stage?.length
                        ? selectedItems?.stage?.indexOf(name.id) > -1
                        : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl> */}
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            id='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? 14 : 16,
            }}
          >
            Category<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            multiple
            required={true}
            value={selectedItems.category}
            onChange={(e) => {
              handleSelection(e, 'category');
              handleInputChange('category', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Category'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length ? (
                    drowdownList?.Category__c?.length &&
                    drowdownList?.Category__c.filter((val: any) => selected?.includes(val))
                      ?.length ? (
                      drowdownList?.Category__c.filter((val: any) => selected?.includes(val))?.join(
                        ', ',
                      )
                    ) : (
                      <Skeleton
                        variant='text'
                        sx={{
                          fontSize: theme.typography.body2,
                          width: '100%',
                          // Optionally adjust other styles here
                        }}
                      />
                    )
                  ) : (
                    ''
                  );
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {drowdownList?.Category__c?.length &&
              drowdownList?.Category__c?.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Checkbox
                    checked={
                      selectedItems?.category?.length
                        ? selectedItems?.category?.indexOf(name) > -1
                        : false
                    }
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            id='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? 14 : 16,
            }}
          >
            Commission (%)<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.gdCommission}
            onChange={(e) => {
              handleSelection(e, 'gdCommission');
              handleInputChange('gdCommission', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Commision (%)'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length ? (
                    drowdownList?.GD_Commission__c?.length &&
                    drowdownList?.GD_Commission__c?.filter((val: any) => val === selected[0])[0] ? (
                      drowdownList?.GD_Commission__c?.filter((val: any) => val === selected[0])[0]
                    ) : (
                      <Skeleton
                        variant='text'
                        sx={{
                          fontSize: theme.typography.body2,
                          width: '100%',
                          // Optionally adjust other styles here
                        }}
                      />
                    )
                  ) : (
                    ''
                  );
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {drowdownList?.GD_Commission__c?.length &&
              drowdownList?.GD_Commission__c?.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={
                      selectedItems?.gdCommission?.length
                        ? selectedItems?.gdCommission?.indexOf(name) > -1
                        : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>
      <Typography variant='heading4'>Development Details</Typography>
      <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name-label'
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? 14 : 16,
            }}
          >
            Production status<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.productionStatus}
            onChange={(e) => {
              handleSelection(e, 'productionStatus');
              handleInputChange('productionStatus', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Production status'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length ? (
                    drowdownList?.Production_Status__c?.length &&
                    drowdownList?.Production_Status__c?.filter(
                      (val: any) => val === selected[0],
                    )[0] ? (
                      drowdownList?.Production_Status__c?.filter(
                        (val: any) => val === selected[0],
                      )[0]
                    ) : (
                      <Skeleton
                        variant='text'
                        sx={{
                          fontSize: theme.typography.body2,
                          width: '100%',
                          // Optionally adjust other styles here
                        }}
                      />
                    )
                  ) : (
                    ''
                  );
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {drowdownList?.Production_Status__c?.length &&
              drowdownList?.Production_Status__c?.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={
                      selectedItems?.productionStatus?.length
                        ? selectedItems?.productionStatus?.indexOf(name) > -1
                        : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            id='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? 14 : 16,
            }}
          >
            Rating<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            // multiple
            disabled={!['Features'].includes(selectedItems?.type[0])}
            required={true}
            value={selectedItems.rating}
            onChange={(e) => {
              handleSelection(e, 'rating');
              handleInputChange('rating', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: `${zeplinColor.Neutral5} !important`,
                    borderWidth: '1px',
                  },
                  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: `${zeplinColor.Neutral5} `,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Rating'
              />
            }
            renderValue={(selected) => {
              return selected?.length ? (
                selected[0] === 'none' ? (
                  'None'
                ) : drowdownList?.Rating__c?.length &&
                  drowdownList?.Rating__c?.filter((val: any) => val === selected[0])[0] ? (
                  drowdownList?.Rating__c?.filter((val: any) => val === selected[0])[0]
                ) : (
                  <Skeleton
                    variant='text'
                    sx={{
                      fontSize: theme.typography.body2,
                      width: '100%',
                    }}
                  />
                )
              ) : (
                ''
              );
            }}
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            <MenuItem key={'none'} value={'none'}>
              <Radio
                checked={
                  selectedItems?.rating?.length
                    ? selectedItems?.rating?.indexOf('none') > -1
                    : false
                } // Check if this radio should be selected
                sx={{
                  color: zeplinColor.BasicBlack,
                  '&.Mui-checked': {
                    color: zeplinColor.BasicBlack,
                  },
                }}
              />
              ----None----
            </MenuItem>
            {drowdownList?.Rating__c?.length &&
              drowdownList?.Rating__c?.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={
                      selectedItems?.rating?.length
                        ? selectedItems?.rating?.indexOf(name) > -1
                        : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name-label'
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? 14 : 16,
            }}
          >
            Episodic Order<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            // multiple
            disabled={!['Series', 'Unscripted'].includes(selectedItems?.type[0])}
            required={true}
            value={selectedItems.episodicOrder}
            onChange={(e) => {
              handleSelection(e, 'episodicOrder');
              handleInputChange('episodicOrder', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: `${zeplinColor.Neutral5} !important`,
                    borderWidth: '1px',
                  },
                  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: `${zeplinColor.Neutral5} `,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Episodic Order'
              />
            }
            renderValue={(selected) => {
              return selected?.length ? (
                selected[0] === 'none' ? (
                  'None'
                ) : drowdownList?.Episodic_Order__c?.length &&
                  drowdownList?.Episodic_Order__c.filter((val: any) => val === selected[0])[0] ? (
                  drowdownList?.Episodic_Order__c.filter((val: any) => val === selected[0])[0]
                ) : (
                  <Skeleton
                    variant='text'
                    sx={{
                      fontSize: theme.typography.body2,
                      width: '100%',
                    }}
                  />
                )
              ) : (
                ''
              );
            }}
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            <MenuItem key={'none'} value={'none'}>
              <Radio
                checked={
                  selectedItems?.episodicOrder?.length
                    ? selectedItems?.episodicOrder?.indexOf('none') > -1
                    : false
                } // Check if this radio should be selected
                sx={{
                  color: zeplinColor.BasicBlack,
                  '&.Mui-checked': {
                    color: zeplinColor.BasicBlack,
                  },
                }}
              />
              ----None----
            </MenuItem>
            {drowdownList?.Episodic_Order__c?.length &&
              drowdownList?.Episodic_Order__c?.map((name: any) => {
                // For Unscripted type, only show episodes 1-20
                if (['Unscripted', 'Series']?.includes(selectedItems?.type[0])) {
                  // Match both "1 Episode" (singular) and "X Episodes" (plural)
                  const match = name.match(/^(\d+) Episode(s?)$/);
                  if (!match) return null; // Not in "X Episode(s)" format

                  const episodeCount = parseInt(match[1]);
                  if (episodeCount < 1 || episodeCount > 20) return null;
                }
                return (
                  <MenuItem key={name} value={name}>
                    <Radio
                      checked={
                        selectedItems?.episodicOrder?.length
                          ? selectedItems?.episodicOrder?.indexOf(name) > -1
                          : false
                      }
                      sx={{
                        color: zeplinColor.BasicBlack,
                        '&.Mui-checked': {
                          color: zeplinColor.BasicBlack,
                        },
                      }}
                    />
                    {name}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>
        <FormControl sx={{ width: '100%' }}>
          <InputLabel
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name-label'
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
              fontSize: isXs ? 14 : 16,
            }}
          >
            Release type<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            size={isXs ? 'small' : 'medium'}
            id='demo-multiple-name'
            // multiple
            disabled={!['Features', 'Unscripted', 'Series'].includes(selectedItems?.type[0])}
            required={true}
            value={selectedItems.releaseType}
            onChange={(e) => {
              handleSelection(e, 'releaseType');
              handleInputChange('releaseType', e.target.value);
            }}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: `${zeplinColor.Neutral5} !important`,
                    borderWidth: '1px',
                  },
                  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: `${zeplinColor.Neutral5} `,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='Release type'
              />
            }
            renderValue={(selected) => {
              return selected?.length ? (
                selected[0] === 'none' ? (
                  'None'
                ) : drowdownList?.Release_Type__c?.length &&
                  drowdownList?.Release_Type__c?.filter((val: any) => val === selected[0])[0] ? (
                  drowdownList?.Release_Type__c?.filter((val: any) => val === selected[0])[0]
                ) : (
                  <Skeleton
                    variant='text'
                    sx={{
                      fontSize: theme.typography.body2,
                      width: '100%',
                    }}
                  />
                )
              ) : (
                ''
              );
            }}
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            <MenuItem key={'none'} value={'none'}>
              <Radio
                checked={
                  selectedItems?.releaseType?.length
                    ? selectedItems?.releaseType?.indexOf('none') > -1
                    : false
                } // Check if this radio should be selected
                sx={{
                  color: zeplinColor.BasicBlack,
                  '&.Mui-checked': {
                    color: zeplinColor.BasicBlack,
                  },
                }}
              />
              ----None----
            </MenuItem>
            {drowdownList?.Release_Type__c?.length &&
              drowdownList?.Release_Type__c?.map((name: any) => {
                // Filter release type options based on selected type
                if (selectedItems?.type[0] === 'Series') {
                  // For Series type, show specific release types
                  const seriesOptions = [
                    'Broadcast',
                    'Cable',
                    'Satellite',
                    'Streaming',
                    'Syndication',
                  ];
                  if (!seriesOptions.includes(name)) {
                    return null;
                  }
                } else if (selectedItems?.type[0] === 'Unscripted') {
                  // For Unscripted type, show limited release types
                  const unscriptedOptions = ['Broadcast', 'Streaming'];
                  if (!unscriptedOptions.includes(name)) {
                    return null;
                  }
                }
                // For Features, show all options (no filtering)

                return (
                  <MenuItem key={name} value={name}>
                    <Radio
                      checked={
                        selectedItems?.releaseType?.length
                          ? selectedItems?.releaseType?.indexOf(name) > -1
                          : false
                      }
                      sx={{
                        color: zeplinColor.BasicBlack,
                        '&.Mui-checked': {
                          color: zeplinColor.BasicBlack,
                        },
                      }}
                    />
                    {name}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        <CustomDatePicker
          size={isXs ? 'small' : 'medium'}
          value={shootStart}
          setValue={(date) => {
            setShootStart(date); // Update local state
            handleInputChange('approxShootDateStart', dayjs(date as Dayjs).format('YYYY-MM-DD')); // Pass the selected date to the parent component
          }}
          // setValue={setShootStart}
          label={'Approx shoot start date'}
        />

        <CustomDatePicker
          value={shootEnd}
          size={isXs ? 'small' : 'medium'}
          setValue={(date) => {
            setShootEnd(date); // Update local state
            handleInputChange('approxShootDateEnd', dayjs(date as Dayjs).format('YYYY-MM-DD')); // Pass the selected date to the parent component
          }}
          // setValue={setShootEnd}
          label={'Approx shoot end date'}
        />
      </Box>
      <FormControl sx={{ width: '100%' }}>
        <InputLabel
          id='demo-multiple-name-label'
          size={isXs ? 'small' : 'medium'}
          sx={{
            '&.Mui-focused': { color: zeplinColor.Neutral9 },
            fontSize: isXs ? 14 : 16,
          }}
        >
          Commitments<span style={{ color: zeplinColor.Error }}></span>
        </InputLabel>
        <Select
          labelId='demo-multiple-name-label'
          size={isXs ? 'small' : 'medium'}
          id='demo-multiple-name'
          // multiple
          disabled={
            !['Features', 'Digital', 'Series', 'Unscripted'].includes(selectedItems?.type[0])
          }
          required={true}
          value={selectedItems.commitments}
          onChange={(e) => {
            handleSelection(e, 'commitments');
            handleInputChange('commitments', e.target.value);
          }}
          input={
            <OutlinedInput
              sx={{
                // Target hover specifically when component is disabled
                '&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: `${zeplinColor.Neutral5} !important`, // Keep disabled color on hover
                  borderWidth: '1px',
                },
                // Regular hover when not disabled

                // Disabled styles
                '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '1px',

                  borderColor: `${zeplinColor.Neutral5} `,
                },
                // Focus styles
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '1px',

                  borderColor: zeplinColor.Neutral9,
                },
              }}
              label='commitments'
            />
          }
          renderValue={
            (selected) =>
              // (selected as string[]).map((val) => (
              {
                return selected?.length ? (
                  selected[0] === 'none' ? (
                    'None'
                  ) : drowdownList?.Commitments__c?.length &&
                    drowdownList?.Commitments__c.filter((val: any) => val === selected[0])[0] ? (
                    drowdownList?.Commitments__c.filter((val: any) => val === selected[0])[0]
                  ) : (
                    <Skeleton
                      variant='text'
                      sx={{
                        fontSize: theme.typography.body2,
                        width: '100%',
                        // Optionally adjust other styles here
                      }}
                    />
                  )
                ) : (
                  ''
                );
              }
            // ))
          }
          MenuProps={MenuProps}
          IconComponent={KeyboardArrowDown}
        >
          <MenuItem key={'none'} value={'none'}>
            <Radio
              checked={
                selectedItems?.commitments?.length
                  ? selectedItems?.commitments?.indexOf('none') > -1
                  : false
              } // Check if this radio should be selected
              sx={{
                color: zeplinColor.BasicBlack,
                '&.Mui-checked': {
                  color: zeplinColor.BasicBlack,
                },
              }}
            />
            ----None----
          </MenuItem>
          {drowdownList?.Commitments__c?.length &&
            drowdownList?.Commitments__c?.map((name: any) => {
              // Filter commitment options based on selected type
              if (selectedItems?.type[0] === 'Unscripted') {
                // For Unscripted type
                const unscriptedOptions = [
                  'In Development',
                  'Mini-Series',
                  'Series Pick Up',
                  'Series Production Commitment',
                  'Special',
                ];
                if (!unscriptedOptions.includes(name)) {
                  return null;
                }
              } else if (selectedItems?.type[0] === 'Series') {
                // For Series type
                const seriesOptions = [
                  'Backdoor Pilot',
                  'In Development',
                  'M.O.W',
                  'Mini-Series',
                  'Pilot',
                  'Pilot + Penalty',
                  'Pilot Pick Up',
                  'Pilot Presentation',
                  'Pilot Presentation Pick Up',
                  'Pilot Production Commitment',
                  'Put Pilot',
                  'Script',
                  'Script + Penalty',
                  'Series of Specials',
                  'Series Pick Up',
                  'Series Production Commitment',
                  'Special',
                  'Spec Script',
                ];
                if (!seriesOptions.includes(name)) {
                  return null;
                }
              } else if (selectedItems?.type[0] === 'Features') {
                // For Features type
                const featuresOptions = [
                  'Greenlight',
                  'Option',
                  'Rewrite',
                  'Script',
                  'Spec Script',
                ];
                if (!featuresOptions.includes(name)) {
                  return null;
                }
              } else if (selectedItems?.type[0] === 'Digital') {
                // For Digital type
                const digitalOptions = [
                  'In Development',
                  'M.O.W',
                  'Mini-Series',
                  'Original Video',
                  'Pilot',
                  'Pilot Pick Up',
                  'Script',
                  'Series Pick Up',
                  'Special',
                ];
                if (!digitalOptions.includes(name)) {
                  return null;
                }
              }

              return (
                <MenuItem key={name} value={name}>
                  <Radio
                    checked={
                      selectedItems?.commitments?.length
                        ? selectedItems?.commitments?.indexOf(name) > -1
                        : false
                    }
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name}
                </MenuItem>
              );
            })}
        </Select>
      </FormControl>
      <Typography variant='heading4'>Services</Typography>
      <CustomTextField
        style={{ width: '100%' }}
        form={true}
        onBlur={handleServicesBlur}
        onChange={(e) => {
          handleServicesChange(e);
          handleInputChange('services', e.target.value);
        }}
        name='Services name'
        text={services}
        error={invalidServices && didServicesChange ? 'Please enter valid Services' : ''}
        label={'Services name'}
        type='text'
        tabindex={1}
        // required
        multiline
      />
      <CustomTextField
        style={{ width: '100%' }}
        form={true}
        onBlur={handleExecutiveProducerBlur}
        onChange={(e) => {
          handleExecutiveProducerChange(e);
          handleInputChange('executiveProducerFee', e.target.value);
        }}
        name='Executive producer services'
        text={executiveProducer}
        error={
          invalidExecutiveProducer && didExecutiveProducerChange
            ? 'Please enter valid Executive'
            : ''
        }
        label={'Executive producer services'}
        type='text'
        tabindex={1}
        // required
        multiline
      />
      <Typography variant='heading4'>Log line</Typography>
      <CustomTextField
        form={true}
        style={{ width: '100%' }}
        onBlur={handleLoglineBlur}
        onChange={(e) => {
          handleLoglineChange(e);
          handleInputChange('logLine', e.target.value);
        }}
        name='Log line'
        text={logline}
        error={invalidLogline && didLoglineChange ? 'Please enter valid Log line' : ''}
        label={'Log line'}
        type='text'
        tabindex={1}
        // required
        multiline
      />
      <Typography variant='heading4'>Terms</Typography>
      {/* <CustomTextField
        style={{ width: '100%' }}
        onBlur={handleCompensationBlur}
        onChange={handleCompensationChange}
        name='Compensation name'
        text={compensation}
        error={
          invalidCompensation && didCompensationChange ? 'Please enter valid Compensation' : ''
        }
        label={'Compensation name'}
        type='text'
        tabindex={1}
        multiline
        // required
      /> */}
      <CustomTextField
        form={true}
        style={{ width: '100%' }}
        onBlur={handleExclusivityBlur}
        onChange={(e) => {
          handleExclusivityChange(e);
          handleInputChange('exclusivityLanguage', e.target.value);
        }}
        name='Exclusivity language'
        text={exclusivity}
        error={invalidExclusivity && didExclusivityChange ? 'Please enter valid Exclusivity' : ''}
        label={'Exclusivity language'}
        type='text'
        tabindex={1}
        multiline
        // required
      />
      <CustomTextField
        form={true}
        style={{ width: '100%' }}
        onBlur={handleOptionsBlur}
        onChange={(e) => {
          handleOptionsChange(e);
          handleInputChange('options', e.target.value);
        }}
        name='Options'
        text={options}
        error={invalidOptions && didOptionsChange ? 'Please enter valid Options' : ''}
        label={'Options'}
        type='text'
        tabindex={1}
        multiline
        // required
      />
      <CustomTextField
        form={true}
        style={{ width: '100%' }}
        onBlur={handleConditionsBlur}
        onChange={(e) => {
          handleConditionsChange(e);
          handleInputChange('conditions', e.target.value);
        }}
        name='Conditions'
        text={conditions}
        error={invalidConditions && didConditionsChange ? 'Please enter valid Conditions' : ''}
        label={'Conditions'}
        type='text'
        tabindex={1}
        multiline
        // required
      />
      <CustomTextField
        form={true}
        style={{ width: '100%' }}
        onBlur={handleRecoupabilityBlur}
        onChange={(e) => {
          handleRecoupabilityChange(e);
          handleInputChange('recoupability', e.target.value);
        }}
        name='Recoupability'
        text={recoupability}
        error={
          invalidRecoupability && didRecoupabilityChange ? 'Please enter valid recoupability' : ''
        }
        label={'Recoupability'}
        type='text'
        tabindex={1}
        multiline
        // required
      />
      <Typography variant='heading4'>Fees</Typography>
      <CustomTextField
        form={true}
        style={{ width: '100%' }}
        onBlur={handleFeesBlur}
        onChange={(e) => {
          handleFeesChange(e);
          handleInputChange('guarantee', e.target.value);
        }}
        name='Guarantee'
        text={fees}
        error={invalidFees && didFeesChange ? 'Please enter valid fees' : ''}
        label={'Guarantee'}
        type='text'
        tabindex={1}
        // required
        multiline
      />
      <CustomTextField
        form={true}
        style={{ width: '100%' }}
        onBlur={handleMAGRBlur}
        onChange={(e) => {
          handleMAGRChange(e);
          handleInputChange('magr', e.target.value);
        }}
        name='MAGR'
        text={mAGR}
        error={invalidMAGR && didMAGRChange ? 'Please enter valid MAGR' : ''}
        label={'MAGR'}
        type='text'
        tabindex={1}
        // required
        multiline
      />
      <CustomTextField
        form={true}
        style={{ width: '100%' }}
        onBlur={handleFeesConditionsBlur}
        onChange={(e) => {
          handleFeesConditionsChange(e);
          handleInputChange('condtions', e.target.value);
        }}
        name='Conditions'
        text={feesConditions}
        error={
          invalidFeesConditions && didFeesConditionsChange ? 'Please enter valid Conditions' : ''
        }
        label={'Conditions'}
        type='text'
        tabindex={1}
        // required
        multiline
      />

      <CustomTextField
        form={true}
        style={{ width: '100%' }}
        onBlur={handleExecutiveProducerFeesBlur}
        onChange={(e) => {
          handleExecutiveProducerFeesChange(e);
          handleInputChange('executiveProducerFee', e.target.value);
        }}
        name='ExecutiveProducerFees'
        text={executiveProducerFees}
        error={
          invalidExecutiveProducerFees && didExecutiveProducerFeesChange
            ? 'Please enter valid Executive producer fees'
            : ''
        }
        label={'Executive producer fees'}
        type='text'
        tabindex={1}
        // required
        multiline
      />
      <Typography variant='heading4'>Additional Information</Typography>
      <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        <CustomTextField
          form={true}
          style={{ width: '100%' }}
          onBlur={() => {
            handleEstimatedBlur && handleEstimatedBlur();
          }}
          onChange={(e) => {
            handleEstimatedChange && handleEstimatedChange(e);
          }}
          name={'Estimated valuation'}
          text={estimated ? estimated : ''}
          error={invalidEstimated && didEstimatedChange ? 'Please enter valid Estimated' : ''}
          label={'Estimated valuation'}
          type='number'
          tabindex={1}
          // max={100}
          // required
        />

        <CustomTextField
          form={true}
          style={{ width: '100%' }}
          onBlur={() => {
            handlePartnerPercentBlur && handlePartnerPercentBlur();
          }}
          onChange={(e) => {
            handlePartnerPercentChange && handlePartnerPercentChange(e);
            handleInputChange('partnerPercent', +e.target.value);
          }}
          name={'Partner percent (%)'}
          text={partnerPercent ? partnerPercent : ''}
          error={
            invalidPartnerPercent && didPartnerPercentChange
              ? 'Please enter valid PartnerPercent'
              : ''
          }
          label={'Partner percent (%)'}
          type='number'
          tabindex={1}
          max={100}
          // required
        />
      </Box>
      <Box sx={{ display: 'flex', gap: '24px', width: '100%' }}>
        {selectedItems?.stageName.includes('Closed Lost') && (
          <FormControl sx={{ width: '100%' }}>
            <InputLabel
              size={isXs ? 'small' : 'medium'}
              id='demo-multiple-name-label'
              sx={{
                '&.Mui-focused': { color: zeplinColor.Neutral9 },
                fontSize: isXs ? 14 : 16,
              }}
            >
              Reason Lost<span style={{ color: zeplinColor.Error }}></span>
            </InputLabel>
            <Select
              labelId='demo-multiple-name-label'
              id='demo-multiple-name'
              // multiple
              required={true}
              value={selectedItems.lostReason}
              onChange={(e) => {
                handleSelection(e, 'lostReason');
                handleInputChange('reasonLost', e.target.value);
              }}
              input={
                <OutlinedInput
                  sx={{
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      color: zeplinColor.Neutral9,
                      borderColor: zeplinColor.Neutral9,
                    },
                  }}
                  label='lostReason'
                />
              }
              renderValue={
                (selected) =>
                  // (selected as string[]).map((val) => (
                  {
                    return selected?.length ? (
                      drowdownList?.Reason_Lost__c?.length &&
                      drowdownList?.Reason_Lost__c?.filter((val: any) => val === selected[0])[0] ? (
                        drowdownList?.Reason_Lost__c?.filter((val: any) => val === selected[0])[0]
                      ) : (
                        <Skeleton
                          variant='text'
                          sx={{
                            fontSize: theme.typography.body2,
                            width: '100%',
                            // Optionally adjust other styles here
                          }}
                        />
                      )
                    ) : (
                      ''
                    );
                  }
                // ))
              }
              MenuProps={MenuProps}
              IconComponent={KeyboardArrowDown}
            >
              {drowdownList?.Reason_Lost__c?.length &&
                drowdownList?.Reason_Lost__c?.map((name: any) => (
                  <MenuItem key={name} value={name}>
                    <Radio
                      checked={
                        selectedItems?.lostReason?.length
                          ? selectedItems?.lostReason?.indexOf(name) > -1
                          : false
                      } // Check if this radio should be selected
                      sx={{
                        color: zeplinColor.BasicBlack,
                        '&.Mui-checked': {
                          color: zeplinColor.BasicBlack,
                        },
                      }}
                    />
                    {name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}
        {/* <FormControl sx={{ width: '100%' }}>
          <InputLabel
            id='demo-multiple-name-label'
            sx={{
              '&.Mui-focused': { color: zeplinColor.Neutral9 },
            }}
          >
            Lead source<span style={{ color: zeplinColor.Error }}></span>
          </InputLabel>
          <Select
            labelId='demo-multiple-name-label'
            id='demo-multiple-name'
            // multiple
            required={true}
            value={selectedItems.leadSource}
            onChange={(e) => handleSelection(e, 'leadSource')}
            input={
              <OutlinedInput
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    color: zeplinColor.Neutral9,
                    borderColor: zeplinColor.Neutral9,
                  },
                }}
                label='leadSource'
              />
            }
            renderValue={
              (selected) =>
                // (selected as string[]).map((val) => (
                {
                  return selected?.length
                    ? leadSourceList?.length &&
                        leadSourceList.filter((val) => val.id === selected[0])[0]?.name
                    : '';
                }
              // ))
            }
            MenuProps={MenuProps}
            IconComponent={KeyboardArrowDown}
          >
            {leadSourceList?.length &&
              leadSourceList.map((name) => (
                <MenuItem key={name.id} value={name.id}>
                  <Radio
                    checked={
                      selectedItems?.stage?.length
                        ? selectedItems?.stage?.indexOf(name.id) > -1
                        : false
                    } // Check if this radio should be selected
                    sx={{
                      color: zeplinColor.BasicBlack,
                      '&.Mui-checked': {
                        color: zeplinColor.BasicBlack,
                      },
                    }}
                  />
                  {name.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl> */}
      </Box>
    </>
  );
};

export default DevelopmentFormField;
